/**
 * The guest list of one performance (fase 2, steg 4 — "gjesteliste").
 *
 * ---------------------------------------------------------------------------------------------
 * IT HANGS OFF A PERFORMANCE, NOT OFF AN EDITION, because the build plan puts it there and because
 * that is what it is: the people an ACT brings. A festival-wide list is a different thing with
 * different owners, and inventing both at once would mean guessing which one a row belongs to
 * every time somebody adds a name.
 *
 * AN ENTRY IS A NAME AND A NUMBER, not a person. Real guest lists read "Kari Nordmann +2", and a
 * row per human would make the door count three names nobody can check off separately anyway.
 * `seats` defaults to 1, so the common case is a name and nothing else.
 *
 * `arrivedAt` IS THE DOOR, and it is an INSTANT rather than a flag. "Came at 20:40" answers
 * questions a boolean cannot — how the queue moved, whether the guest arrived before the set, who
 * was still outstanding at curfew — and it costs the same column. Null means not yet.
 *
 * ---------------------------------------------------------------------------------------------
 * IT CONSUMES `performance:read` AND `performance:update`, AND THAT IS A DECISION.
 *
 * Not `performance_guest:*`. Four new cells would appear in the Settings panel with nothing behind
 * them until somebody wired each one, which is exactly what Atle asked to hold off on (question E,
 * 29 August). The guest list is part of the booking: whoever may edit the performance may edit who
 * it brings.
 *
 * WHAT THAT COSTS, written down rather than discovered: anybody who can edit a booking can read its
 * guest list. For a production tool that is close to right — the people editing bookings are the
 * ones running the door — but a festival that wants the list narrower than the booking has to say
 * so, and then it becomes its own resource. In `docs/TIL-GODKJENNING.md`.
 * ---------------------------------------------------------------------------------------------
 */
import { z } from 'zod';

import { guestKindSchema } from '../enums.js';
import { cursorQuerySchema, page } from '../http/pagination.js';
import { booleanQuerySchema } from './common.js';
import { rowViewerSchema } from './row-viewer.js';

/**
 * One sort, and it is the door's.
 *
 * A guest list is read at a door by somebody looking for a name, so it is ordered by name and
 * nothing else. Arrival order is a different question that `arrivedAt` already answers, and a
 * second sort would need a second index for a list that is rarely more than fifty rows.
 */
/**
 * Fire ordener, ikke én (Atle, 2. september: «se også til at alle tabeller og slikt kan sorteres»).
 *
 * Kontrakten sa lenge at gjestelista «is read at a door by somebody looking for a name, so it is
 * ordered by name and nothing else». Det er sant om DØRA. Det er ikke sant om kontoret, som spør
 * hvem som ikke har kommet ennå, hvem som tar med flest plasser, og hvor mange av navnene som er
 * presse — tre spørsmål lista ikke kunne svare på.
 *
 * `kind` ordner etter enumets egen rekkefølge og GRUPPERER derfor framfor å alfabetisere. Det er
 * med vilje: den som klikker på den kolonnen vil se artistene samlet, ikke i bokstavorden.
 */
export const GUEST_SORT_KEYS = ['name', 'kind', 'seats', 'arrived'] as const;
export type GuestSortKey = (typeof GUEST_SORT_KEYS)[number];

/** A guest list rarely runs to fifty. This is the ceiling before the request is refused. */
export const MAX_GUEST_SEATS = 100;

export const performanceGuestDtoSchema = z
  .object({
    id: z.uuid(),
    performanceId: z.uuid(),
    name: z.string(),
    kind: guestKindSchema,
    /** How many people this one name brings, including themselves. */
    seats: z.number().int().positive(),
    note: z.string().nullable(),
    /** When the FIRST of them came through the door. Null = nobody yet. */
    arrivedAt: z.iso.datetime().nullable(),
    /**
     * Hvor mange av `seats` som har kommet (215.20, migrering 0382). «Kari + 3» kommer ofte i to
     * puljer; én knapp for hele følget ga døra et tall som var feil halve kvelden.
     */
    arrivedSeats: z.number().int().min(0),
    /** Hva leseren kan gjøre med raden (D1), regnet av `can()` på serveren. */
    viewer: rowViewerSchema.optional(),
  })
  .meta({ id: 'PerformanceGuest' });

export const listPerformanceGuestsQuerySchema = cursorQuerySchema(GUEST_SORT_KEYS)
  .extend({
    /** Whose list. Required: a guest list without a performance is not a thing this holds. */
    performanceId: z.uuid(),
    /**
     * `?arrived=false` is the door's working view — who is still outstanding. Omitting it is the
     * whole list, which is what the office reads.
     *
     * `booleanQuerySchema` and not an enum of two strings. The first draft of this invented its
     * own, which would have made `?arrived` mean nothing where `?unread` on the notification feed
     * means true, and `?arrived=1` a 400 where `?unread=1` is fine. One shape for one job — the
     * rule `common.ts` states about itself.
     */
    arrived: booleanQuerySchema.optional(),
  })
  .meta({ id: 'ListPerformanceGuestsQuery' });

export const performanceGuestListResponseSchema = page(
  performanceGuestDtoSchema,
  'PerformanceGuestListResponse',
);

export const createPerformanceGuestRequestSchema = z
  .object({
    performanceId: z.uuid(),
    name: z.string().trim().min(1).max(200),
    kind: guestKindSchema.optional(),
    seats: z.number().int().min(1).max(MAX_GUEST_SEATS).optional(),
    note: z.string().trim().max(1000).nullish(),
  })
  .meta({ id: 'CreatePerformanceGuestRequest' });

/**
 * Everything except which performance it belongs to.
 *
 * No `performanceId`, for the reason `updatePerformanceRequestSchema` has no `editionId`: a guest
 * who moved to another booking would leave the door list they were checked in against.
 *
 * `arrivedAt` IS SETTABLE AND CLEARABLE, and that is the door's own correction. Somebody marked
 * arrived by mistake has to be un-marked by the person at the door, at the door, without finding
 * an administrator — so `null` is a legal value rather than an irreversible stamp.
 */
export const updatePerformanceGuestRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    kind: guestKindSchema.optional(),
    seats: z.number().int().min(1).max(MAX_GUEST_SEATS).optional(),
    note: z.string().trim().max(1000).nullish(),
    arrivedAt: z.iso.datetime().nullish(),
    /**
     * Plass for plass (215.20). Serveren setter `arrivedAt` når den første krysses av og nuller den
     * ved 0. Sendes bare `arrivedAt`, gjelder den hele følget, som før.
     */
    arrivedSeats: z.number().int().min(0).max(MAX_GUEST_SEATS).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdatePerformanceGuestRequest' });

/**
 * `GET /v1/performances/:id/guest-totals` — the two numbers the door actually asks for.
 *
 * ITS OWN ROUTE, not a field on the list response, and the precedent is
 * `GET /v1/notifications/unread-count`: an aggregate over the whole set is a different question
 * from a page of it, and hanging it on the page would make page four pay for a number only page
 * one renders — and answer it about the wrong rows.
 *
 * `seats` is not `items.length`. A page of twelve names can be forty people, and "how many are we
 * expecting" is what somebody on the door needs before the queue arrives.
 *
 * It is not a `COUNT(*)` standing in for pagination, which principle 6 forbids: the page still
 * comes back by cursor, and this is two sums over a list that is rarely more than fifty rows and
 * hard-capped by the schema at a hundred seats an entry.
 */
export const performanceGuestTotalsSchema = z
  .object({
    /** Every seat on the list, arrived or not. */
    seats: z.number().int().min(0),
    /** Seats checked in, summed seat by seat (215.20). */
    arrivedSeats: z.number().int().min(0),
    entries: z.number().int().min(0),
    /**
     * Plassene KONTRAKTEN avtalte, eller null før noen har skrevet en (QA H10).
     *
     * Skilt fra `seats`, som er summen av lista slik den står. De to svarer på hver sitt spørsmål
     * — «hvor mange har vi lovet bort» og «hvor mange har vi brukt» — og lista uten taket er det
     * tallet ingen kan handle på.
     */
    agreedSeats: z.number().int().nullable(),
    /**
     * DE TYPENE ORGANISASJONEN ALLEREDE HAR BRUKT.
     *
     * Atle, 2. september: «De som allerede er brukt lagres i dropdownmenyen.» Since the kind became
     * free text (0056) this is what keeps the column countable: the second person to write
     * «Plateselskap» picks it from the list rather than inventing «plateselskap».
     *
     * ORGANISATION-WIDE, not this booking's, and that is the point — a vocabulary the whole house
     * shares. It rides on this response rather than on a route of its own because the panel already
     * asks for the totals and a second round trip for six words is a second round trip for six
     * words.
     */
    kinds: z.array(z.string()),
  })
  .meta({ id: 'PerformanceGuestTotals' });

export type PerformanceGuestDto = z.infer<typeof performanceGuestDtoSchema>;
export type ListPerformanceGuestsQuery = z.infer<typeof listPerformanceGuestsQuerySchema>;
export type PerformanceGuestListResponse = z.infer<typeof performanceGuestListResponseSchema>;
export type CreatePerformanceGuestRequest = z.infer<typeof createPerformanceGuestRequestSchema>;
export type UpdatePerformanceGuestRequest = z.infer<typeof updatePerformanceGuestRequestSchema>;
export type PerformanceGuestTotals = z.infer<typeof performanceGuestTotalsSchema>;
